/* global Word */

import { reportResult, paraDetail, RunDetail, applydefaultparagraphformat } from '../runs/common';
import { getConfig } from '../taskpane/configService';
import { ensureRunStyles } from '../taskpane/styleEngine';


// ═══════════════════════════════════════════════════════════════════════════
// RUN 10: Heading Styling (TOC-Validated)
// ═══════════════════════════════════════════════════════════════════════════

const EXCLUDE = [
  'toc', 'table of contents', 'lof', 'lot', 'loa', 'index',
  'caption', 'footer', 'header', 'title', 'subtitle',
  'footnote', 'endnote', 'bibliography', 'references',
  'hyperlink', 'followed hyperlink', 'internet link',
  'table:', 'table text', 'table:text', 'table:header',
  'table header', 'table footnote', 'table:footnote'
];

const LIST_STYLES = [
  'list bullet', 'list number', 'list alpha', 'list paragraph',
  'table:bullet', 'table:list', 'bulleted list', 'numbered list'
];

interface TOCEntry {
  number: string;
  normalizedTitle: string;
  level: number;
  rawText: string;
}

function shouldExclude(s: string): boolean {
  if (!s) return false;
  const l = s.toLowerCase().trim();
  return EXCLUDE.some(x => l.includes(x));
}

function isListStyle(s: string): boolean {
  if (!s) return false;
  const l = s.toLowerCase().trim();
  return LIST_STYLES.some(x => l.includes(x));
}

function isHeadingStyle(s: string): boolean {
  if (!s) return false;
  return /^heading\s*[1-9]$/i.exec(s.trim()) !== null;
}

function detectHeadingNumber(text: string, maxHeadingLevel: number): { match: boolean; level: number; fullNumber: string } {
  if (!text?.trim()) return { match: false, level: 0, fullNumber: "" };
  const m = /^(\d+(?:\.\d+)*)\.?\s+(\S)/.exec(text.trim());
  if (!m) return { match: false, level: 0, fullNumber: "" };
  const segs = m[1].split('.');
  let level: number;
  if (segs.length === 1) level = 1;
  else if (segs[segs.length - 1] === '0') level = Math.max(1, segs.length - 1);
  else level = segs.length;
  return { match: true, level: Math.min(level, maxHeadingLevel), fullNumber: m[1] };
}

function normalizeForCompare(text: string): string {
  if (!text) return "";
  return text.trim()
    .replace(/^\d+(?:\.\d+)*\.?\s*/, "")
    .replace(/\s+\d+\s*$/, "")
    .replace(/\t/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

function extractHeadingNumber(text: string): string {
  if (!text) return "";
  const m = /^(\d+(?:\.\d+)*)\.?\s/.exec(text.trim());
  return m ? m[1] : "";
}

function wordSimilarity(a: string, b: string): number {
  if (!a || !b) return 0;
  const wordsA = a.split(/\s+/).filter(w => w.length > 0);
  const wordsB = b.split(/\s+/).filter(w => w.length > 0);
  if (wordsA.length === 0 || wordsB.length === 0) return 0;
  const commonWords = wordsA.filter(w => wordsB.includes(w));
  return commonWords.length / Math.max(wordsA.length, wordsB.length);
}

// ═══════════════════════════════════════════════════════════════════════════
// w:hyperlink tag detection (raw OOXML check)
// ═══════════════════════════════════════════════════════════════════════════
//
// Checks the actual <w:hyperlink> OOXML element on a paragraph, rather than
// relying only on style-name substrings ("hyperlink"/"link" in para.style),
// which can miss real hyperlink-wrapped runs or false-positive on styles that
// merely contain those words. Distinguishes:
//   - internal anchor links (w:anchor="...") — how Word's auto-generated TOC
//     links a TOC line to its target heading bookmark
//   - external relationship links (r:id="...") — normal "go to a URL" links
// ═══════════════════════════════════════════════════════════════════════════

interface HyperlinkTagInfo {
  hasTag: boolean;
  isInternalAnchor: boolean;
  isExternalRelId: boolean;
}

async function detectHyperlinkTag(
  para: Word.Paragraph,
  context: Word.RequestContext
): Promise<HyperlinkTagInfo> {
  const range = para.getRange(Word.RangeLocation.whole);
  const ooxmlResult = range.getOoxml();
  await context.sync();

  const xml = ooxmlResult.value || "";
  const tagMatch = /<w:hyperlink\b([^>]*)>/.exec(xml);

  if (!tagMatch) {
    return { hasTag: false, isInternalAnchor: false, isExternalRelId: false };
  }

  const attrs = tagMatch[1];
  const isInternalAnchor = /\bw:anchor=["'][^"']+["']/.test(attrs);
  const isExternalRelId = /\br:id=["'][^"']+["']/.test(attrs);

  return { hasTag: true, isInternalAnchor, isExternalRelId };
}

// ═══════════════════════════════════════════════════════════════════════════
// PHASE 1: Extract TOC entries
// ═══════════════════════════════════════════════════════════════════════════

function processTOCEntry(
  text: string,
  maxHeadingLevel: number,
  tocEntries: TOCEntry[],
  tocByNumber: Map<string, TOCEntry>,
  tocByTitle: Map<string, TOCEntry>
): void {
  const headingNum = extractHeadingNumber(text);
  const normTitle = normalizeForCompare(text);
  const hLevel = detectHeadingNumber(text, maxHeadingLevel);

  if (!headingNum || !normTitle) return;

  const entry: TOCEntry = {
    number: headingNum,
    normalizedTitle: normTitle,
    level: hLevel.level,
    rawText: text.substring(0, 80)
  };
  tocEntries.push(entry);
  tocByNumber.set(headingNum, entry);
  tocByTitle.set(normTitle, entry);
}

// FIX [Ln 116 complexity]: Extracted condition into dedicated helper
//
// hasHyperlinkTag param: result of detectHyperlinkTag() for this paragraph.
// Logic:
//   - If the paragraph carries a real w:hyperlink tag but does NOT look like
//     a numbered TOC line (no numbered pattern, no tab+pagenum pattern),
//     it's treated as an ordinary in-body hyperlink (e.g. an external
//     reference link) and EXCLUDED from TOC-entry detection.
//   - Internal anchor hyperlinks (Word's auto-generated TOC links) combined
//     with a numbered pattern, while inside the TOC section, are treated as
//     TOC entries — this is the normal case for an auto-generated TOC.
function isTOCEntryParagraph(
  text: string,
  style: string,
  tocSectionFound: boolean,
  tocSectionEnded: boolean,
  hyperlinkInfo: HyperlinkTagInfo
): boolean {
  const isTOCStyle = style.includes('toc') || style.includes('table of contents');
  const isTOCPattern = /^\d+(?:\.\d+)*\.?\s/.exec(text) !== null && /\t\d+\s*$/.exec(text) !== null;
  const isInTOCSection = tocSectionFound && !tocSectionEnded;
  const hasNumberedPattern = /^\d+(?:\.\d+)*\.?\s+\S/.exec(text) !== null;
  const isHyperlinkInTOC = isInTOCSection &&
    (style.includes('hyperlink') || style.includes('link') || hyperlinkInfo.hasTag);

  // Exclude: real w:hyperlink tag present, but doesn't look like a numbered
  // TOC line at all → ordinary in-body hyperlink, not a TOC entry.
  if (hyperlinkInfo.hasTag && !hasNumberedPattern && !isTOCPattern) {
    return false;
  }

  return isTOCStyle || isTOCPattern || (isInTOCSection && hasNumberedPattern) || (isHyperlinkInTOC && hasNumberedPattern);
}

async function extractTOCEntries(
  paragraphs: Word.ParagraphCollection,
  totalParas: number,
  maxHeadingLevel: number,
  context: Word.RequestContext
): Promise<{ tocEntries: TOCEntry[]; tocByNumber: Map<string, TOCEntry>; tocByTitle: Map<string, TOCEntry> }> {
  console.log("── PHASE 1: Reading TOC entries ──\n");

  const tocEntries: TOCEntry[] = [];
  const tocByNumber = new Map<string, TOCEntry>();
  const tocByTitle = new Map<string, TOCEntry>();

  let tocSectionFound = false;
  let tocSectionEnded = false;
  let consecutiveNonTOC = 0;

  const NO_HYPERLINK: HyperlinkTagInfo = { hasTag: false, isInternalAnchor: false, isExternalRelId: false };

  for (let i = 0; i < totalParas; i++) {
    const para = paragraphs.items[i];
    const text = (para.text || "").trim();
    const style = (para.style || "").toLowerCase().trim();

    if (!text) continue;

    const textUpper = text.toUpperCase().replace(/\s+/g, " ");
    if (textUpper === "TABLE OF CONTENTS" || textUpper === "CONTENTS" ||
        textUpper === "TABLE OF CONTENT") {
      tocSectionFound = true;
      consecutiveNonTOC = 0;
      console.log(`  TOC section marker found at paragraph ${i + 1}: "${text}"`);
      continue;
    }

    // Only inspect OOXML for hyperlink tags while inside the TOC section,
    // to avoid an extra getOoxml()+sync() round-trip per paragraph across
    // the whole document (expensive on large docs).
    const isInTOCSection = tocSectionFound && !tocSectionEnded;
    const hyperlinkInfo = isInTOCSection
      ? await detectHyperlinkTag(para, context)
      : NO_HYPERLINK;

    if (isTOCEntryParagraph(text, style, tocSectionFound, tocSectionEnded, hyperlinkInfo)) {
      consecutiveNonTOC = 0;
      processTOCEntry(text, maxHeadingLevel, tocEntries, tocByNumber, tocByTitle);
    } else if (isInTOCSection) {
      consecutiveNonTOC++;
      if (consecutiveNonTOC >= 3) {
        tocSectionEnded = true;
        console.log(`  TOC section ends around paragraph ${i + 1}`);
      }
    }
  }

  console.log(`\n  TOC entries found: ${tocEntries.length}`);
  for (const entry of tocEntries) {
    console.log(`    [${entry.number}] L${entry.level}: "${entry.normalizedTitle}"`);
  }

  return { tocEntries, tocByNumber, tocByTitle };
}

// ═══════════════════════════════════════════════════════════════════════════
// TOC Sanity Validation
// ═══════════════════════════════════════════════════════════════════════════
//
// Checks whether the TOC itself is internally consistent BEFORE it is used
// to validate/convert body headings. A corrupted TOC (duplicate numbers,
// backwards/out-of-order sequence, or large gaps) silently clobbers entries
// in tocByNumber/tocByTitle (Map.set overwrites on collision), which causes
// body paragraphs to get matched against the WRONG TOC entry and converted
// incorrectly (e.g. "3.0 Title Page" getting relabeled toward "5.0 Synopsis").
//
// Only top-level (level === 1) entries are checked. Sub-levels (5.1, 5.2...)
// are expected to branch/restart and are not part of this check.
// ═══════════════════════════════════════════════════════════════════════════

interface TOCValidationResult {
  isConsistent: boolean;
  issues: string[];
}

function validateTOCConsistency(tocEntries: TOCEntry[]): TOCValidationResult {
  const issues: string[] = [];

  const topLevel = tocEntries.filter(e => e.level === 1);

  // 1. Duplicate top-level numbers (e.g. two entries both claiming "5.0")
  const seen = new Map<string, string[]>();
  for (const e of topLevel) {
    const key = e.number.split('.')[0]; // "5.0" -> "5", "5" -> "5"
    if (!seen.has(key)) seen.set(key, []);
    seen.get(key).push(e.normalizedTitle);
  }
  for (const [num, titles] of Array.from(seen.entries())) {
    if (titles.length > 1) {
      issues.push(`Duplicate top-level number "${num}" used for: ${titles.join(' / ')}`);
    }
  }

  // 2. Backwards / out-of-order sequence (e.g. "5.0" appears before "3.0")
  const numericSeq = topLevel.map(e => parseInt(e.number.split('.')[0], 10));
  for (let i = 1; i < numericSeq.length; i++) {
    if (numericSeq[i] < numericSeq[i - 1]) {
      issues.push(`Sequence goes backwards: "${topLevel[i - 1].number}" followed by "${topLevel[i].number}"`);
    }
  }

  // 3. Gaps larger than 1 (e.g. "1.0" jumps straight to "4.0")
  for (let i = 1; i < numericSeq.length; i++) {
    if (numericSeq[i] - numericSeq[i - 1] > 1) {
      issues.push(`Gap in numbering: "${topLevel[i - 1].number}" jumps to "${topLevel[i].number}"`);
    }
  }

  return { isConsistent: issues.length === 0, issues };
}

// ═══════════════════════════════════════════════════════════════════════════
// TOC Matching logic
// ═══════════════════════════════════════════════════════════════════════════

function matchByNumber(
  headingNumber: string,
  normParaTitle: string,
  tocByNumber: Map<string, TOCEntry>
): TOCEntry | null {
  if (!headingNumber || !tocByNumber.has(headingNumber)) return null;
  const candidate = tocByNumber.get(headingNumber);
  const tocTitle = candidate.normalizedTitle;

  if (normParaTitle === tocTitle) return candidate;
  if (tocTitle.length > 2 && normParaTitle.length > 2) {
    if (normParaTitle.startsWith(tocTitle) || tocTitle.startsWith(normParaTitle)) return candidate;
  }
  if (wordSimilarity(normParaTitle, tocTitle) >= 0.6) return candidate;

  const tocWords = tocTitle.split(/\s+/);
  if (tocWords.length <= 2 && (normParaTitle === tocTitle || normParaTitle.startsWith(tocTitle + " "))) {
    return candidate;
  }

  console.log(`    [DEBUG] Number "${headingNumber}" matched but title didn't → REJECTED`);
  return null;
}

function matchByExactTitle(
  headingNumber: string,
  normParaTitle: string,
  tocByTitle: Map<string, TOCEntry>
): TOCEntry | null {
  if (!tocByTitle.has(normParaTitle)) return null;
  const candidate = tocByTitle.get(normParaTitle);
  if (!headingNumber || candidate.number === headingNumber) return candidate;
  console.log(`    [DEBUG] Title matched but number differs → REJECTED`);
  return null;
}

function matchByFuzzyTitle(
  headingNumber: string,
  normParaTitle: string,
  tocByTitle: Map<string, TOCEntry>
): TOCEntry | null {
  const tocTitleEntries = Array.from(tocByTitle.entries());
  for (const [tocTitle, entry] of tocTitleEntries) {
    if (tocTitle.length <= 3 || normParaTitle.length <= 3) continue;
    if (headingNumber && entry.number !== headingNumber) continue;
    if (normParaTitle.startsWith(tocTitle) || tocTitle.startsWith(normParaTitle)) return entry;
    if (wordSimilarity(normParaTitle, tocTitle) >= 0.7) return entry;
  }
  return null;
}

function matchesTOCEntry(
  headingNumber: string,
  paragraphText: string,
  tocByNumber: Map<string, TOCEntry>,
  tocByTitle: Map<string, TOCEntry>
): TOCEntry | null {
  const normParaTitle = normalizeForCompare(paragraphText);
  if (!normParaTitle) return null;

  return matchByNumber(headingNumber, normParaTitle, tocByNumber)
    ?? matchByExactTitle(headingNumber, normParaTitle, tocByTitle)
    ?? matchByFuzzyTitle(headingNumber, normParaTitle, tocByTitle);
}

// ═══════════════════════════════════════════════════════════════════════════
// Hyperlink detection (style/property based — used in Phase 2 body scan)
// ═══════════════════════════════════════════════════════════════════════════

async function hasHyperlink(para: Word.Paragraph, context: Word.RequestContext): Promise<boolean> {
  const ranges = para.getTextRanges([' ', '\t', '\n'], true);
  ranges.load('items');
  await context.sync();
  for (const r of ranges.items) {
    r.load('style, hyperlink');
    await context.sync();
    const rs = (r.style || '').toLowerCase();
    if (rs.includes('hyperlink') || rs.includes('link') || r.hyperlink) return true;
  }
  return false;
}

// ═══════════════════════════════════════════════════════════════════════════
// Character style clearing
// ═══════════════════════════════════════════════════════════════════════════

async function stripDuplicateNumber(
  para: Word.Paragraph,
  context: Word.RequestContext
): Promise<boolean> {
  const li = para.listItemOrNullObject;
  li.load("isNullObject, listString");
  await context.sync();

  if (!li.isNullObject && li.listString) {
    para.load("text");
    await context.sync();
    const nm = /^(\d+(?:\.\d+)*\.?\s+)/.exec(para.text || "");
    if (nm) {
      const clean = (para.text || "").substring(nm[0].length);
      console.log(`    Strip dup number: "${nm[0].trim()}"`);
      context.document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;
      await context.sync();
      para.getRange(Word.RangeLocation.content).insertText(clean, Word.InsertLocation.replace);
      await context.sync();
      return true;
    }
  }
  return false;
}

async function stripExplicitOffProps(
  para: Word.Paragraph,
  context: Word.RequestContext
): Promise<boolean> {
  const range = para.getRange(Word.RangeLocation.whole);
  const ooxmlResult = range.getOoxml();
  await context.sync();

  const original = ooxmlResult.value;
  const TAGS_TO_CHECK = ['i', 'iCs', 'b', 'bCs'];
  let patched = original;
  let anyChange = false;

  function patchRprBlock(rprBlock: string): string {
    let modified = rprBlock;
    let blockChanged = false;

    for (const tag of TAGS_TO_CHECK) {
      const valZeroRe = new RegExp(
        String.raw`<w:${tag}\b[^>]*\bw:val=["']0["'][^>]*/?>(?:</w:${tag}>)?`,
        'g'
      );
      if (valZeroRe.exec(modified)) {
        modified = modified.replace(valZeroRe, '');
        blockChanged = true;
      }

      const bareRe = new RegExp(
        String.raw`<w:${tag}\s*/>`,
        'g'
      );
      if (bareRe.exec(modified)) {
        modified = modified.replace(bareRe, '');
        blockChanged = true;
      }
    }

    if (!blockChanged) return rprBlock;
    anyChange = true;

    const innerOriginal = rprBlock
      .replace(/^<w:rPr\b[^>]*>/, '')
      .replace(/<\/w:rPr>$/, '');

    const rPrChange = `<w:rPrChange w:id="0" w:author="QC" w:date="1970-01-01T00:00:00Z"><w:rPr>${innerOriginal}</w:rPr></w:rPrChange>`;

    return modified.replace(/<\/w:rPr>$/, `${rPrChange}</w:rPr>`);
  }

  const PPR_PLACEHOLDER = '___PPR_BLOCK___';
  const pprBlocks: string[] = [];

  patched = patched.replace(/<w:pPr\b[^>]*>[\s\S]*?<\/w:pPr>/g, (pprBlock) => {
    pprBlocks.push(pprBlock);
    return PPR_PLACEHOLDER;
  });

  patched = patched.replace(/<w:rPr\b[^>]*>[\s\S]*?<\/w:rPr>/g, patchRprBlock);

  let pprIdx = 0;
  patched = patched.replace(new RegExp(PPR_PLACEHOLDER, 'g'), () => {
    const pprBlock = pprBlocks[pprIdx++];
    return pprBlock.replace(/<w:rPr\b[^>]*>[\s\S]*?<\/w:rPr>/g, patchRprBlock);
  });

  if (!anyChange) return false;

  context.document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;
  await context.sync();
  range.insertOoxml(patched, Word.InsertLocation.replace);
  await context.sync();

  return true;
}

// ═══════════════════════════════════════════════════════════════════════════
// PHASE 2: Process body paragraphs
// ═══════════════════════════════════════════════════════════════════════════

interface HeadingCounters {
  totalChecked: number;
  headingsConverted: number;
  charStylesFixed: number;
  numbersStripped: number;
  skippedTOC: number;
  skippedNoMatch: number;
  skippedOther: number;
  explicitOffPropsFixed: number;
}

interface ProcessHeadingContext {
  tocByNumber: Map<string, TOCEntry>;
  tocByTitle: Map<string, TOCEntry>;
  context: Word.RequestContext;
  counters: HeadingCounters;
  changedParas: RunDetail[];
  maxHeadingLevel: number;
}

function shouldSkipParagraph(paraText: string, paraStyle: string, counters: HeadingCounters): boolean {
  if (!paraText) return true;
  const styleLower = paraStyle.toLowerCase();
  if (styleLower.includes('toc') || styleLower.includes('table of contents')) {
    counters.skippedTOC++;
    return true;
  }
  if (/^\d+(?:\.\d+)*\.?\s/.exec(paraText) !== null && /\t\d+\s*$/.exec(paraText) !== null) {
    counters.skippedTOC++;
    return true;
  }
  if (shouldExclude(paraStyle) || isListStyle(paraStyle)) {
    counters.skippedOther++;
    return true;
  }
  return false;
}

async function handleAlreadyCorrectHeading(
  para: Word.Paragraph,
  paraStyle: string,
  targetStyle: string,
  targetLevel: number,
  textHasManualNumber: boolean,
  ctx: ProcessHeadingContext,
  index: number
): Promise<void> {
  console.log(`  [${index}] ALREADY "${paraStyle}" — applying default paragraph format`);
  await ctx.context.sync();
  await applydefaultparagraphformat(para, targetStyle, ctx.context);
  ctx.counters.charStylesFixed++;

  if (textHasManualNumber) {
    if (await stripDuplicateNumber(para, ctx.context)) ctx.counters.numbersStripped++;
  }
  if (targetLevel === 5 || targetLevel === 6) {
    console.log("ENTEREDDDDDDDDDDDD IN HERRRRRRRRRRRRRRRREEEEEEEEE " + para.text);
    const fixed = await stripExplicitOffProps(para, ctx.context);
    if (fixed) {
      ctx.counters.explicitOffPropsFixed++;
      console.log(`  [${index}] H${targetLevel} — stripped explicit bold/italic=off overrides`);
    }
  }
}

async function handleHeadingConversion(
  para: Word.Paragraph,
  fullText: string,
  targetStyle: string,
  targetLevel: number,
  textHasManualNumber: boolean,
  ctx: ProcessHeadingContext,
  index: number
): Promise<void> {
  console.log(`\n  [${index}] CONVERT: "${fullText.substring(0, 70)}"`);
  console.log(`    "${para.style}" → "${targetStyle}" | L${targetLevel}`);

  if (textHasManualNumber) {
    para.load("text");
    await ctx.context.sync();
    const nm = /^(\d+(?:\.\d+)*\.?\s+)/.exec(para.text || "");
    if (nm) {
      const clean = (para.text || "").substring(nm[0].length);
      console.log(`    Strip manual number: "${nm[0].trim()}"`);
      ctx.context.document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;
      await ctx.context.sync();
      para.getRange(Word.RangeLocation.content).insertText(clean, Word.InsertLocation.replace);
      await ctx.context.sync();
      ctx.counters.numbersStripped++;
    }
  }

  ctx.context.document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;
  await ctx.context.sync();
  para.style = targetStyle;
  await ctx.context.sync();

  await applydefaultparagraphformat(para, targetStyle, ctx.context);
  console.log(`    ✓ Converted + default format applied!`);

  ctx.changedParas.push(paraDetail(para.uniqueLocalId, para.text || "", "→ " + targetStyle));
  ctx.counters.headingsConverted++;
}

// FIX [Ln 528 complexity]: Extracted per-paragraph logic into processOneParagraph
async function processOneParagraph(
  para: Word.Paragraph,
  index: number,
  ctx: ProcessHeadingContext
): Promise<void> {
  const paraText = (para.text || "").trim();
  const paraStyle = (para.style || "").trim();

  if (shouldSkipParagraph(paraText, paraStyle, ctx.counters)) return;

  // ── Unnumbered heading: already a Heading style but no numeric prefix ──
  // e.g. "VALIDATION" styled as Heading1 with rogue Emphasis char style.
  // No TOC match is possible, but we still need to apply default format.
  if (isHeadingStyle(paraStyle)) {
    const hcCheck = detectHeadingNumber(paraText, ctx.maxHeadingLevel);
    if (!hcCheck.match) {
      console.log(`  [${index}] UNNUMBERED HEADING "${paraStyle}" — applying default format: "${paraText.substring(0, 60)}"`);
      await ctx.context.sync();
      await applydefaultparagraphformat(para, paraStyle, ctx.context);
      ctx.counters.charStylesFixed++;
      return;
    }
  }
  // ──────────────────────────────────────────────────────────────────────

  const listItem = para.listItemOrNullObject;
  listItem.load("isNullObject, listString");
  await ctx.context.sync();
  const listString = (!listItem.isNullObject && listItem.listString) ? listItem.listString.trim() : "";
  const fullText = listString ? `${listString} ${paraText}`.trim() : paraText.trim();

  const hc = detectHeadingNumber(fullText, ctx.maxHeadingLevel);
  if (!hc.match) return;

  const tocMatch = matchesTOCEntry(hc.fullNumber, fullText, ctx.tocByNumber, ctx.tocByTitle);
  if (!tocMatch) {
    ctx.counters.skippedNoMatch++;
    console.log(`  [${index}] SKIP (not in TOC): "${fullText.substring(0, 60)}"`);
    return;
  }

  const targetLevel = hc.level;
  const targetStyle = `Heading ${targetLevel}`;

  const hasHL = await hasHyperlink(para, ctx.context);
  if (hasHL) {
    ctx.counters.skippedOther++;
    console.log(`  [${index}] SKIP (hyperlink): "${fullText.substring(0, 60)}"`);
    return;
  }

  const textHasManualNumber = /^\d+(?:\.\d+)*\.?\s+\S/.exec(paraText.trim()) !== null;

  if (isHeadingStyle(paraStyle) && paraStyle.trim().toLowerCase() === targetStyle.toLowerCase()) {
    await handleAlreadyCorrectHeading(para, paraStyle, targetStyle, targetLevel, textHasManualNumber, ctx, index);
  } else {
    await handleHeadingConversion(para, fullText, targetStyle, targetLevel, textHasManualNumber, ctx, index);
  }
}

async function processHeadingParagraphs(
  paragraphs: Word.ParagraphCollection,
  totalParas: number,
  ctx: ProcessHeadingContext
): Promise<void> {
  console.log("\n── PHASE 2: Processing body paragraphs ──\n");
  for (let i = 0; i < totalParas; i++) {
    ctx.counters.totalChecked++;
    await processOneParagraph(paragraphs.items[i], i + 1, ctx);
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// Main export
// ═══════════════════════════════════════════════════════════════════════════

export async function run10() {
  await Word.run(async (context) => {
    try {
      await ensureRunStyles(context, 10);

      const cfg = getConfig();
      const maxHeadingLevel = cfg.run10_headings.maxHeadingLevel;

      context.document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;

      const counters: HeadingCounters = {
        totalChecked: 0, headingsConverted: 0, charStylesFixed: 0,
        numbersStripped: 0, skippedTOC: 0, skippedNoMatch: 0, skippedOther: 0,
        explicitOffPropsFixed: 0,
      };
      const changedParas: RunDetail[] = [];

      console.log("=".repeat(70));
      console.log(`=== HEADING STYLE CHECKER (TOC-VALIDATED) — ${cfg.clientName} ===`);
      console.log(`Max heading level: ${maxHeadingLevel}`);
      console.log("=".repeat(70));

      const paragraphs = context.document.body.paragraphs;
      paragraphs.load("items");
      await context.sync();
      const totalParas = paragraphs.items.length;
      console.log(`Total paragraphs: ${totalParas}\n`);

      for (let i = 0; i < totalParas; i++) { paragraphs.items[i].load("text, style, uniqueLocalId"); }
      await context.sync();

      const { tocEntries, tocByNumber, tocByTitle } = await extractTOCEntries(paragraphs, totalParas, maxHeadingLevel, context);

      if (tocEntries.length === 0) {
        console.log("  ⚠ No TOC entries found. Skipping heading conversion.");
        reportResult('Heading Styling — no TOC found', [{ label: 'No Table of Contents detected', note: 'Heading conversion skipped' }]);
        return;
      }

      // ── TOC consistency check (must pass before any matching/conversion) ──
      const tocValidation = validateTOCConsistency(tocEntries);
      if (!tocValidation.isConsistent) {
        console.log("  ⚠ TOC IS IMPROPER / INCONSISTENT. Skipping heading conversion.");
        tocValidation.issues.forEach(issue => console.log(`    - ${issue}`));
        reportResult('⚠ TOC IS IMPROPER OR INCONSISTENT — Heading conversion skipped', [
          { label: 'Reason', note: 'Detected duplicate, out-of-order, or gapped numbering in the Table of Contents' },
          ...tocValidation.issues.map(issue => ({ label: 'Issue', note: issue })),
          { label: 'TOC entries found', count: tocEntries.length },
        ]);
        return;
      }
      // ────────────────────────────────────────────────────────────────────

      const processCtx: ProcessHeadingContext = {
        tocByNumber, tocByTitle, context, counters, changedParas, maxHeadingLevel
      };
      await processHeadingParagraphs(paragraphs, totalParas, processCtx);

      context.document.changeTrackingMode = Word.ChangeTrackingMode.off;
      await context.sync();

      console.log("\n" + "=".repeat(70));
      console.log("=== HEADING STYLE CHECK SUMMARY ===");
      console.log(`Paragraphs checked:       ${counters.totalChecked}`);
      console.log(`TOC entries found:        ${tocEntries.length}`);
      console.log(`Headings converted:       ${counters.headingsConverted}`);
      console.log(`Char styles cleared:      ${counters.charStylesFixed}`);
      console.log(`Numbers stripped:         ${counters.numbersStripped}`);
      console.log(`Skipped (TOC):            ${counters.skippedTOC}`);
      console.log(`Skipped (not in TOC):     ${counters.skippedNoMatch}`);
      console.log(`Skipped (other):          ${counters.skippedOther}`);
      console.log("=".repeat(70));
      console.log("✓ Heading style check complete!");

      if (counters.headingsConverted > 0 || counters.explicitOffPropsFixed > 0 || counters.charStylesFixed > 0) {
        reportResult('Heading Styling Complete', [
          { label: 'Headings converted to correct style', count: counters.headingsConverted },
          { label: 'Character styles cleared (incl. unnumbered headings)', count: counters.charStylesFixed },
          { label: 'Duplicate numbers stripped', count: counters.numbersStripped },
          { label: 'TOC entries matched', count: tocEntries.length },
          { label: 'H5/H6 bold/italic=off overrides removed', count: counters.explicitOffPropsFixed },
          ...changedParas,
        ]);
      } else {
        reportResult('Heading Styling — no changes needed', [
          { label: 'All headings already correct', note: 'No changes made' },
          { label: 'TOC entries found', count: tocEntries.length }
        ]);
      }

    } catch (error) {
      console.error("Error in run10:", error);
      console.error("Message:", error.message);
      if (error.debugInfo) console.error("Debug:", JSON.stringify(error.debugInfo));
    }
  });
}